import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './CompleteSignup.css';

/**
 * CompleteSignup Page
 *
 * Handles the magic link flow after payment:
 * 1. Validates the token from URL query params
 * 2. Prompts user to create Auth0 account
 * 3. Links Auth0 account to Stripe subscription
 * 4. Redirects to app
 */
const CompleteSignup = () => {
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('validating'); // validating, valid, invalid, linking, complete, error
  const [tokenData, setTokenData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Step 1: Validate token on mount
  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setErrorMessage('No signup token found in URL.');
      return;
    }

    const validateToken = async () => {
      try {
        const response = await fetch(`${API_URL}/api/signup/validate-token/${token}`);
        const data = await response.json();

        if (data.valid) {
          setStatus('valid');
          setTokenData(data);

          // If not authenticated, trigger Auth0 signup
          if (!isAuthenticated) {
            // Use a small delay to show the "valid" message
            setTimeout(() => {
              loginWithRedirect({
                authorizationParams: {
                  screen_hint: 'signup',
                  login_hint: data.email
                },
                appState: { returnTo: `/complete-signup?token=${token}` }
              });
            }, 1500);
          }
        } else {
          setStatus('invalid');
          setErrorMessage(data.error || 'Invalid or expired signup link.');
        }
      } catch (error) {
        console.error('Token validation error:', error);
        setStatus('error');
        setErrorMessage('Failed to validate signup link. Please try again.');
      }
    };

    validateToken();
  }, [token, isAuthenticated, loginWithRedirect, API_URL]);

  // Step 2: Once authenticated, complete signup by linking accounts
  useEffect(() => {
    if (isAuthenticated && user && status === 'valid' && tokenData) {
      const completeSignup = async () => {
        setStatus('linking');

        try {
          const response = await fetch(`${API_URL}/api/signup/complete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              token,
              auth0_sub: user.sub,
              email: user.email
            })
          });

          const data = await response.json();

          if (data.success) {
            setStatus('complete');
            // Redirect to app after short delay
            setTimeout(() => {
              navigate('/app');
            }, 2000);
          } else {
            setStatus('error');
            setErrorMessage(data.error || 'Failed to complete signup.');
          }
        } catch (error) {
          console.error('Signup completion error:', error);
          setStatus('error');
          setErrorMessage('Failed to link your account. Please contact support.');
        }
      };

      completeSignup();
    }
  }, [isAuthenticated, user, status, tokenData, token, navigate, API_URL]);

  // Helper to render step icon
  const renderStepIcon = (stepNumber, isActive, isCompleted, isLoading) => {
    if (isLoading) {
      return <span className="material-icons step-icon spinning">sync</span>;
    }
    if (isCompleted) {
      return <span className="material-icons step-icon">check</span>;
    }
    return stepNumber;
  };

  // Determine step states
  const step1Completed = status !== 'validating' && status !== 'invalid' && status !== 'error';
  const step1Loading = status === 'validating';
  const step2Active = status === 'linking' || status === 'complete';
  const step2Completed = status === 'complete';
  const step2Loading = status === 'linking';
  const step3Active = status === 'complete';

  // Render states
  return (
    <div className="complete-signup-container">
      <div className="complete-signup-card">
        {/* Logo */}
        <div className="signup-logo">
          <img src={require('../images/footerlogo.webp')} alt="Humanaize" />
        </div>

        {/* Progress Steps */}
        <div className="signup-steps">
          <div className={`step ${step1Completed || step1Loading ? 'active' : ''} ${status === 'invalid' || status === 'error' ? '' : ''}`}>
            <div className="step-circle">
              {renderStepIcon(1, true, step1Completed, step1Loading)}
            </div>
            <span>Betaling geverifieerd</span>
          </div>
          <div className="step-line"></div>
          <div className={`step ${step2Active ? 'active' : ''}`}>
            <div className="step-circle">
              {renderStepIcon(2, step2Active, step2Completed, step2Loading)}
            </div>
            <span>Account aanmaken</span>
          </div>
          <div className="step-line"></div>
          <div className={`step ${step3Active ? 'active' : ''}`}>
            <div className="step-circle">
              {renderStepIcon(3, step3Active, step3Active, false)}
            </div>
            <span>Toegang verleend</span>
          </div>
        </div>

        {/* Status Messages */}
        <div className="signup-content">
          {status === 'validating' && (
            <div className="status-message">
              <div className="spinner"></div>
              <h2>Betaling verifiëren...</h2>
              <p>Even geduld terwijl we je abonnement bevestigen.</p>
            </div>
          )}

          {status === 'valid' && !isAuthenticated && (
            <div className="status-message">
              <div className="status-icon success">
                <span className="material-icons">check</span>
              </div>
              <h2>Betaling bevestigd!</h2>
              <p>Je wordt doorgestuurd om je account aan te maken...</p>
              <div className="email-info">
                <span className="material-icons">email</span>
                {tokenData?.email}
              </div>
            </div>
          )}

          {status === 'linking' && (
            <div className="status-message">
              <div className="spinner"></div>
              <h2>Account instellen...</h2>
              <p>Je abonnement wordt gekoppeld en toegang tot Job wordt verleend.</p>
            </div>
          )}

          {status === 'complete' && (
            <div className="status-message">
              <div className="status-icon success pulse">
                <span className="material-icons">check</span>
              </div>
              <h2>Welkom bij Job!</h2>
              <p>Je account is klaar. Je wordt doorgestuurd naar de app...</p>
              <div className="trial-info">
                <span className="material-icons">schedule</span>
                <span>7-daagse gratis proefperiode actief</span>
              </div>
            </div>
          )}

          {status === 'invalid' && (
            <div className="status-message error">
              <div className="status-icon error">
                <span className="material-icons">close</span>
              </div>
              <h2>Ongeldige registratielink</h2>
              <p>{errorMessage}</p>
              <div className="error-help">
                <h3>Wat kun je doen?</h3>
                <ul>
                  <li>Controleer je email voor de juiste link</li>
                  <li>Links verlopen na 24 uur</li>
                  <li>Elke link kan maar één keer gebruikt worden</li>
                </ul>
                <a href="/" className="btn-home">
                  <span className="material-icons">arrow_back</span>
                  Terug naar home
                </a>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="status-message error">
              <div className="status-icon warning">
                <span className="material-icons">warning</span>
              </div>
              <h2>Er ging iets mis</h2>
              <p>{errorMessage}</p>
              <div className="error-actions">
                <button
                  onClick={() => window.location.reload()}
                  className="btn-retry"
                >
                  <span className="material-icons">refresh</span>
                  Opnieuw proberen
                </button>
                <a href="mailto:support@humanaize.nl" className="btn-support">
                  <span className="material-icons">support_agent</span>
                  Contact support
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Security Note */}
        <div className="security-note">
          <span className="material-icons">lock</span>
          <span>Je betaling en accountgegevens zijn beveiligd door Stripe en Auth0</span>
        </div>
      </div>
    </div>
  );
};

export default CompleteSignup;
